import { createAction } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { BillingDTO } from "./models/dto";

export namespace BillingActions {
  export enum ActionTypes {
    getPricing = "Billing: Get Pricing",
    setPricing = "Billing: Set Pricing",
    resetPricing = "Billing: Reset Pricing",

    getBalance = "Billing: Get Balance",
    setBalance = "Billing: Set Balance",
    resetBalance = "Billing: Reset Balance",

    getChainBilling = "Billng: Get Chain Billing",
    setChainBilling = "Billng: Set Chain Billing",
    resetChainBilling = "Billng: Reset Chain Billing",

    getVaultsBilling = "Billng: Get Vaults Billing",
    setVaultsBilling = "Billng: Set Vaults Billing",
    resetVaultsBilling = "Billng: Reset Vaults Billing",

    getVerificationBilling = "Billng: Get Verification Billing",
    setVerificationBilling = "Billng: Set Verification Billing",
    resetVerificationBilling = "Billng: Reset Verification Billing",

    getSKRTAddresses = "Billng: Get SKRT Addresses",
    setSKRTAddresses = "Billng: Set SKRT Addresses",
    resetSKRTAddresses = "Billng: Reset SKRT Addresses",

    getForexRate = "Billing: Get Forex Rate",
    resetForexRate = "Billing: Reset Forex Rate",
    setForexRate = "Billing: Set Forex Rate",

    getAllTransactions = "Billing: Get All Individual Transactions",
    setAllTransactions = "Billing: Set All Individual Transactions",
    resetAllTransactions = "Billing: Reset All Individual Transactions",
    getSingleTransaction = "Billing: Get Single Individual Transactions",
    setSingleTransaction = "Billing: Set Single Individual Transactions",
    setAllTransactionsPagination = "Billing: Set All Individual Transactions Pagination",

    getAllUsages = "Billing: Get All Individual Usages",
    setAllUsages = "Billing: Set All Individual Usages",
    resetAllUsages = "Billing: Reset All Individual Usages",
    getSingleUsage = "Billing: Get Single Individual Usages",
    setSingleUsage = "Billing: Set Single Individual Usages",
    setAllUsagesPagination = "Billing: Set All Individual Usages Pagination",
  }

  export const getForexRate = createAction(ActionTypes.getForexRate);
  export const resetForexRate = createAction(ActionTypes.resetForexRate);
  export const setForexRate = createAction<
    FetchableSuccess<BillingDTO.Responses.GetForexRate.T> | FetchableError,
    ActionTypes.setForexRate
  >(ActionTypes.setForexRate);

  export const getSKRTAddresses = createAction(ActionTypes.getSKRTAddresses);
  export const resetSKRTAddresses = createAction(
    ActionTypes.resetSKRTAddresses
  );
  export const setSKRTAddresses = createAction<
    FetchableSuccess<BillingDTO.Responses.GetSKRTAddresses.T> | FetchableError,
    ActionTypes.setSKRTAddresses
  >(ActionTypes.setSKRTAddresses);

  export const getPricing = createAction(ActionTypes.getPricing);
  export const resetPricing = createAction(ActionTypes.resetPricing);
  export const setPricing = createAction<
    FetchableSuccess<BillingDTO.Responses.GetPricing.T> | FetchableError,
    ActionTypes.setPricing
  >(ActionTypes.setPricing);

  export const getBalance = createAction(ActionTypes.getBalance);
  export const resetBalance = createAction(ActionTypes.resetBalance);
  export const setBalance = createAction<
    FetchableSuccess<BillingDTO.Responses.GetBalance.T> | FetchableError,
    ActionTypes.setBalance
  >(ActionTypes.setBalance);

  export const getChainBilling = createAction(ActionTypes.getChainBilling);
  export const resetChainBilling = createAction(ActionTypes.resetChainBilling);
  export const setChainBilling = createAction<
    FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T> | FetchableError,
    ActionTypes.setChainBilling
  >(ActionTypes.setChainBilling);

  export const getVaultsBilling = createAction(ActionTypes.getVaultsBilling);
  export const resetVaultsBilling = createAction(
    ActionTypes.resetVaultsBilling
  );
  export const setVaultsBilling = createAction<
    FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T> | FetchableError,
    ActionTypes.setVaultsBilling
  >(ActionTypes.setVaultsBilling);

  export const getVerificationBilling = createAction(
    ActionTypes.getVerificationBilling
  );
  export const resetVerificationBilling = createAction(
    ActionTypes.resetVerificationBilling
  );
  export const setVerificationBilling = createAction<
    FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T> | FetchableError,
    ActionTypes.setVerificationBilling
  >(ActionTypes.setVerificationBilling);

  // Transaction List
  export const resetAllTransactions = createAction(
    ActionTypes.resetAllTransactions
  );

  export const getAllTransactions = createAction(
    ActionTypes.getAllTransactions
  );

  export const setAllTransactions = createAction<
    | FetchableSuccess<BillingDTO.Responses.GetAllTransactions.T>
    | FetchableError,
    ActionTypes.setAllTransactions
  >(ActionTypes.setAllTransactions);

  export const setAllTransactionsPagination = createAction<
    Pageable.T<
      Exclude<keyof BillingDTO.Responses.GetSingleTransaction.T, "id">
    >,
    ActionTypes.setAllTransactionsPagination
  >(ActionTypes.setAllTransactionsPagination);

  export const getSingleTransaction = createAction<
    BillingDTO.Responses.GetSingleTransaction.T["id"],
    ActionTypes.getSingleTransaction
  >(ActionTypes.getSingleTransaction);

  export const setSingleTransaction = createAction<
    { id: string; result: BillingDTO.Responses.GetSingleTransaction.T },
    ActionTypes.setSingleTransaction
  >(ActionTypes.setSingleTransaction);

  // Usage List
  export const resetAllUsages = createAction(ActionTypes.resetAllUsages);

  export const getAllUsages = createAction(ActionTypes.getAllUsages);

  export const setAllUsages = createAction<
    FetchableSuccess<BillingDTO.Responses.GetAllUsages.T> | FetchableError,
    ActionTypes.setAllUsages
  >(ActionTypes.setAllUsages);

  export const setAllUsagesPagination = createAction<
    Pageable.T<
      Exclude<
        keyof BillingDTO.Responses.GetSingleUsage.T,
        "id" | "featureNames"
      >
    >,
    ActionTypes.setAllUsagesPagination
  >(ActionTypes.setAllUsagesPagination);

  export const getSingleUsage = createAction<
    BillingDTO.Responses.GetSingleUsage.T["id"],
    ActionTypes.getSingleUsage
  >(ActionTypes.getSingleUsage);

  export const setSingleUsage = createAction<
    { id: string; result: BillingDTO.Responses.GetSingleUsage.T },
    ActionTypes.setSingleUsage
  >(ActionTypes.setSingleUsage);

  export type Actions =
    | ReturnType<typeof getPricing>
    | ReturnType<typeof setPricing>
    | ReturnType<typeof resetPricing>
    | ReturnType<typeof getBalance>
    | ReturnType<typeof setBalance>
    | ReturnType<typeof resetBalance>
    | ReturnType<typeof getChainBilling>
    | ReturnType<typeof resetChainBilling>
    | ReturnType<typeof setChainBilling>
    | ReturnType<typeof getVaultsBilling>
    | ReturnType<typeof resetVaultsBilling>
    | ReturnType<typeof setVaultsBilling>
    | ReturnType<typeof getVerificationBilling>
    | ReturnType<typeof resetVerificationBilling>
    | ReturnType<typeof setForexRate>
    | ReturnType<typeof resetForexRate>
    | ReturnType<typeof getForexRate>
    | ReturnType<typeof setSKRTAddresses>
    | ReturnType<typeof resetSKRTAddresses>
    | ReturnType<typeof getSKRTAddresses>
    | ReturnType<typeof setVerificationBilling>
    | ReturnType<typeof resetAllTransactions>
    | ReturnType<typeof getAllTransactions>
    | ReturnType<typeof setAllTransactions>
    | ReturnType<typeof setAllTransactionsPagination>
    | ReturnType<typeof getSingleTransaction>
    | ReturnType<typeof setSingleTransaction>
    | ReturnType<typeof resetAllUsages>
    | ReturnType<typeof getAllUsages>
    | ReturnType<typeof setAllUsages>
    | ReturnType<typeof setAllUsagesPagination>
    | ReturnType<typeof getSingleUsage>
    | ReturnType<typeof setSingleUsage>;
}
